import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  message,
  Modal,
  Card,
  Avatar,
  Tooltip,
  Dropdown,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DollarOutlined,
  ReloadOutlined,
  DownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { expensesApi, recurringExpensesApi } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { getLast30DaysJalaliApiRange } from '../../utils/dateUtils';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS } from '../../constants/ui';
import { useListReturnState } from '../../hooks/useListReturnState';
import { formatMoneyFa } from '../../utils/formatPriceToman';

const { Option } = Select;

const toPersianNumber = (num) => {
  if (num === null || num === undefined) return '-';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

const Expenses = () => {
  const navigate = useNavigate();
  const { canAdd, canEditRecord, canDeleteRecord } = useResourcePermissions('expenses');
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  // پیش‌فرض: 30 روز گذشته
  const defaultRange = getLast30DaysJalaliApiRange();
  const [filters, setFilters] = useState({
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
    status: 'all',
    employee_id: 'all',
    project_id: 'all',
    category_id: 'all',
    search: '',
  });
  const [filterOptions, setFilterOptions] = useState({
    employees: [],
    projects: [],
    categories: [],
  });

  const captureCurrentListState = useCallback(
    () => ({
      filters,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    }),
    [filters, pagination.current, pagination.pageSize],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(restored.filters);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current: restored.pagination.current || 1,
        pageSize: restored.pagination.pageSize || prev.pageSize,
      }));
    }
  });

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const res = await expensesApi.getAll(filters);
      console.log('Expenses API Response:', res);

      if (res?.expenses) {
        setExpenses(res.expenses);
        setPagination({
          ...pagination,
          total: res.pagination?.total || res.expenses.length,
          current: res.pagination?.current_page || 1,
        });

        if (res.filters) {
          setFilterOptions({
            employees: res.filters.employees || [],
            projects: res.filters.projects || [],
            categories: res.filters.categories || [],
          });
        }
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      message.error('خطا در دریافت لیست هزینه‌ها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [filters]);

  const [recurringAlerts, setRecurringAlerts] = useState([]);

  const fetchAlerts = async () => {
    try {
      const res = await recurringExpensesApi.getAlerts();
      if (res?.data?.alerts) setRecurringAlerts(res.data.alerts);
      else if (res?.alerts) setRecurringAlerts(res.alerts);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, []);

  const handleSearch = (value) => {
    setFilters({ ...filters, search: value });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>حذف هزینه</span>,
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          آیا از حذف این هزینه اطمینان دارید؟ این عملیات قابل بازگشت نیست.
        </span>
      ),
      okText: 'بله، حذف کن',
      cancelText: 'لغو',
      okButtonProps: { danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } },
      cancelButtonProps: { style: { borderColor: '#d9d9d9', color: '#595959' } },
      onOk: async () => {
        try {
          await expensesApi.delete(id);
          message.success('هزینه با موفقیت حذف شد');
          fetchExpenses();
        } catch (error) {
          message.error('خطا در حذف هزینه');
        }
      },
    });
  };

  const handleStatusChange = async (id, newStatus) => {
    setStatusLoadingId(id);
    try {
      await expensesApi.updateStatus(id, newStatus);
      message.success('وضعیت هزینه با موفقیت تغییر کرد');
      fetchExpenses();
    } catch (error) {
      message.error('خطا در تغییر وضعیت هزینه');
    } finally {
      setStatusLoadingId(null);
    }
  };

  // عملیات گروهی - حذف
  const handleBulkDelete = () => {
    Modal.confirm({
      title: (
        <span style={{ fontFamily: 'CustomFont' }}>حذف هزینه‌ها</span>
      ),
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          آیا از حذف {toPersianNumber(selectedRowKeys.length)} هزینه انتخاب شده اطمینان دارید؟
        </span>
      ),
      okText: 'بله، حذف کن',
      cancelText: 'لغو',
      okButtonProps: { danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } },
      cancelButtonProps: { style: { borderColor: '#d9d9d9', color: '#595959' } },
      onOk: async () => {
        setBulkActionLoading(true);
        try {
          await expensesApi.applyQuickAction('delete', selectedRowKeys);
          message.success('هزینه‌ها با موفقیت حذف شدند');
          setSelectedRowKeys([]);
          fetchExpenses();
        } catch (error) {
          message.error('خطا در حذف هزینه‌ها');
        } finally {
          setBulkActionLoading(false);
        }
      },
    });
  };

  // عملیات گروهی - تغییر وضعیت
  const handleBulkStatusChange = (newStatus) => {
    const statusLabels = {
      pending: 'در انتظار',
      approved: 'تأیید شده',
      rejected: 'رد شده',
    };
    Modal.confirm({
      title: (
        <span style={{ fontFamily: 'CustomFont' }}>تغییر وضعیت هزینه‌ها</span>
      ),
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          آیا از تغییر وضعیت {toPersianNumber(selectedRowKeys.length)} هزینه به «{statusLabels[newStatus]}» اطمینان دارید؟
        </span>
      ),
      okText: 'بله، تغییر بده',
      cancelText: 'لغو',
      okButtonProps: { type: 'primary', style: { backgroundColor: '#1890ff', borderColor: '#1890ff', color: '#fff' } },
      cancelButtonProps: { style: { borderColor: '#d9d9d9', color: '#595959' } },
      onOk: async () => {
        setBulkActionLoading(true);
        try {
          await expensesApi.applyQuickAction('change-status', selectedRowKeys, newStatus);
          message.success('وضعیت هزینه‌ها با موفقیت تغییر کرد');
          setSelectedRowKeys([]);
          fetchExpenses();
        } catch (error) {
          message.error('خطا در تغییر وضعیت هزینه‌ها');
        } finally {
          setBulkActionLoading(false);
        }
      },
    });
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: {
        color: 'warning',
        text: 'در انتظار',
        icon: <ClockCircleOutlined />,
      },
      approved: {
        color: 'success',
        text: 'تأیید شده',
        icon: <CheckCircleOutlined />,
      },
      rejected: {
        color: 'error',
        text: 'رد شده',
        icon: <CloseCircleOutlined />,
      },
    };
    const s = statusMap[status] || { color: 'default', text: status, icon: null };
    return (
      <Tag color={s.color} icon={s.icon} style={{ fontFamily: 'CustomFont' }}>
        {s.text}
      </Tag>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return toPersianNumber(date.toLocaleDateString('fa-IR'));
  };

  // منوی عملیات گروهی
  const bulkActionMenuItems = [
    {
      key: 'delete',
      label: <span style={{ fontFamily: 'CustomFont' }}>حذف انتخاب‌شده‌ها</span>,
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleBulkDelete,
    },
    { type: 'divider' },
    {
      key: 'status-pending',
      label: <span style={{ fontFamily: 'CustomFont' }}>تغییر به «در انتظار»</span>,
      icon: <ClockCircleOutlined />,
      onClick: () => handleBulkStatusChange('pending'),
    },
    {
      key: 'status-approved',
      label: <span style={{ fontFamily: 'CustomFont' }}>تغییر به «تأیید شده»</span>,
      icon: <CheckCircleOutlined />,
      onClick: () => handleBulkStatusChange('approved'),
    },
    {
      key: 'status-rejected',
      label: <span style={{ fontFamily: 'CustomFont' }}>تغییر به «رد شده»</span>,
      icon: <CloseCircleOutlined />,
      danger: true,
      onClick: () => handleBulkStatusChange('rejected'),
    },
  ];

  const columns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>نام هزینه</span>,
      dataIndex: 'item_name',
      key: 'item_name',
      render: (text) => (
        <span className="font-medium" style={{ fontFamily: 'CustomFont' }}>
          {text}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>مبلغ</span>,
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => (
        <span className="text-blue-600 font-medium" style={{ fontFamily: 'CustomFont' }}>
          {formatMoneyFa(price, record.currency?.currency_symbol)}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>کارمند</span>,
      dataIndex: 'user',
      key: 'user',
      render: (user) =>
        user ? (
          <Space>
            <Avatar src={user.image_url} size="small">
              {user.name?.[0]}
            </Avatar>
            <span style={{ fontFamily: 'CustomFont' }}>{user.name}</span>
          </Space>
        ) : (
          '-'
        ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>خریداری از</span>,
      dataIndex: 'purchase_from',
      key: 'purchase_from',
      render: (text) => (
        <span style={{ fontFamily: 'CustomFont' }}>{text || '-'}</span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>تاریخ خرید</span>,
      dataIndex: 'purchase_date',
      key: 'purchase_date',
      render: (date) => (
        <span style={{ fontFamily: 'CustomFont' }}>{formatDate(date)}</span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>,
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const statusMenuItems = [
          {
            key: 'pending',
            label: <span style={{ fontFamily: 'CustomFont' }}>در انتظار</span>,
            icon: <ClockCircleOutlined />,
            onClick: () => handleStatusChange(record.id, 'pending'),
          },
          {
            key: 'approved',
            label: <span style={{ fontFamily: 'CustomFont' }}>تأیید</span>,
            icon: <CheckCircleOutlined />,
            onClick: () => handleStatusChange(record.id, 'approved'),
          },
          {
            key: 'rejected',
            label: <span style={{ fontFamily: 'CustomFont' }}>رد</span>,
            icon: <CloseCircleOutlined />,
            danger: true,
            onClick: () => handleStatusChange(record.id, 'rejected'),
          },
        ];
        return (
          <Dropdown
            menu={{ items: statusMenuItems }}
            trigger={['click']}
          >
            <Button
              type="text"
              loading={statusLoadingId === record.id}
              style={{ padding: 0 }}
            >
              {getStatusTag(status)}
            </Button>
          </Dropdown>
        );
      },
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>عملیات</span>,
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="مشاهده">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigateToDetail(`/finance/expenses/${record.id}`)}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            />
          </Tooltip>
          {canEditRecord(record) ? (
            <Tooltip title="ویرایش">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => navigateToDetail(`/finance/expenses/${record.id}/edit`)}
                className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
              />
            </Tooltip>
          ) : null}
          {canDeleteRecord(record) ? (
            <Tooltip title="حذف">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
                className="hover:bg-red-50 dark:hover:bg-red-900/20"
              />
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          {/* Header Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                  <DollarOutlined className="text-red-600 dark:text-red-400 text-xl" />
                </div>
                <div>
                  <h1
                    className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    مدیریت هزینه‌ها
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                    ثبت و پیگیری هزینه‌های سازمان
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                <Tooltip title="بروزرسانی">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchExpenses}
                    size="middle"
                    loading={loading}
                  />
                </Tooltip>
                <Button
                  icon={<SyncOutlined />}
                  onClick={() => navigate('/finance/recurring-expenses')}
                  size="middle"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  هزینه‌های دوره‌ای
                </Button>
                {canAdd ? (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate('/finance/expenses/add')}
                    size="middle"
                    className="bg-green-600 hover:bg-green-700 border-0 shadow-md hover:shadow-lg transition-all"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    ثبت هزینه جدید
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {recurringAlerts.length > 0 && (
              <div className="mb-6">
                <Alert
                  message={
                    <span style={{ fontFamily: 'CustomFont', fontWeight: 'bold' }}>
                      هشدارهای سررسید هزینه‌های دوره‌ای
                    </span>
                  }
                  description={
                    <ul className="pl-4 m-0 mt-2" style={{ fontFamily: 'CustomFont' }}>
                      {recurringAlerts.map((alert, index) => (
                        <li key={index}>
                          {alert.item_name} - مبلغ: {formatMoneyFa(alert.price, alert.currency?.currency_symbol)} - تاریخ سررسید: {toPersianNumber(new Date(alert.next_expense_date).toLocaleDateString('fa-IR'))}
                        </li>
                      ))}
                    </ul>
                  }
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  style={{ backgroundColor: '#fffbe6', borderColor: '#ffe58f' }}
                />
              </div>
            )}
            
            {loading ? (
              <TableSkeleton rows={5} columns={6} />
            ) : (
              <>
                {/* Filters */}
                <div className="mb-6 flex flex-wrap gap-3 items-center">
                  <Input
                    placeholder="جستجو..."
                    allowClear
                    value={filters.search}
                    onChange={(e) => handleSearch(e.target.value)}
                    style={{ fontFamily: 'CustomFont', maxWidth: '250px', width: '100%' }}
                    size="middle"
                    prefix={<SearchOutlined className="text-gray-400" />}
                    className="rounded-lg"
                  />

                  <Select
                    placeholder="وضعیت"
                    value={filters.status === 'all' ? undefined : filters.status}
                    onChange={(value) =>
                      handleFilterChange('status', value || 'all')
                    }
                    style={{ fontFamily: 'CustomFont', minWidth: '130px' }}
                    allowClear
                  >
                    <Option value="pending">در انتظار</Option>
                    <Option value="approved">تأیید شده</Option>
                    <Option value="rejected">رد شده</Option>
                  </Select>

                  <Select
                    placeholder="کارمند"
                    value={
                      filters.employee_id === 'all'
                        ? undefined
                        : filters.employee_id
                    }
                    onChange={(value) =>
                      handleFilterChange('employee_id', value || 'all')
                    }
                    style={{ fontFamily: 'CustomFont', minWidth: '160px' }}
                    showSearch
                    optionFilterProp="children"
                    allowClear
                  >
                    {filterOptions.employees.map((emp) => (
                      <Option key={emp.id} value={emp.id}>
                        {emp.name}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    placeholder="پروژه"
                    value={
                      filters.project_id === 'all'
                        ? undefined
                        : filters.project_id
                    }
                    onChange={(value) =>
                      handleFilterChange('project_id', value || 'all')
                    }
                    style={{ fontFamily: 'CustomFont', minWidth: '160px' }}
                    showSearch
                    optionFilterProp="children"
                    allowClear
                  >
                    {filterOptions.projects.map((proj) => (
                      <Option key={proj.id} value={proj.id}>
                        {proj.project_name}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    placeholder="دسته‌بندی"
                    value={
                      filters.category_id === 'all'
                        ? undefined
                        : filters.category_id
                    }
                    onChange={(value) =>
                      handleFilterChange('category_id', value || 'all')
                    }
                    style={{ fontFamily: 'CustomFont', minWidth: '160px' }}
                    showSearch
                    optionFilterProp="children"
                    allowClear
                  >
                    {filterOptions.categories.map((cat) => (
                      <Option key={cat.id} value={cat.id}>
                        {cat.category_name}
                      </Option>
                    ))}
                  </Select>
                </div>

                {/* عملیات گروهی */}
                {selectedRowKeys.length > 0 && (
                  <div className={BULK_ACTION_BAR_CLASS}>
                    <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
                      {toPersianNumber(selectedRowKeys.length)} مورد انتخاب شده
                    </span>
                    <Dropdown
                      menu={{ items: bulkActionMenuItems }}
                      trigger={['click']}
                    >
                      <Button
                        type="primary"
                        loading={bulkActionLoading}
                        size="middle"
                        className="border-0"
                        icon={<DownOutlined />}
                        style={{ fontFamily: 'CustomFont', backgroundColor: '#f97316', color: '#fff' }}
                      >
                        عملیات گروهی
                      </Button>
                    </Dropdown>
                  </div>
                )}

                {/* Table */}
                <Card className="dark:bg-gray-800 border-0 shadow-sm overflow-x-auto">
                  <Table
                    columns={columns}
                    dataSource={expenses}
                    rowKey="id"
                    rowSelection={rowSelection}
                    loading={loading}
                    pagination={{
                      ...pagination,
                      showSizeChanger: true,
                      showTotal: (total) => (
                        <span style={{ fontFamily: 'CustomFont' }}>
                          مجموع: {toPersianNumber(total)} هزینه
                        </span>
                      ),
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1200 }}
                  />
                </Card>
              </>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default Expenses;
